// src/components/upload/UploadQueueModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, ProgressBar, Badge, Alert } from 'react-bootstrap';
import { useUploadQueue } from '../../contexts/UploadQueueContext';
import { formatFileSize } from '../../services/file.service';

const UploadQueueModal = () => {
  const {
    uploadQueue,
    isQueueVisible,
    isQueueMinimized,
    removeFromQueue,
    clearCompleted,
    clearAll,
    moveItemUp,
    moveItemDown,
    retryUpload,
    retryAllFailed,
    hideQueue,
    toggleMinimized,
    getQueueStats,
    cancelUploadWithCleanup
  } = useUploadQueue();

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragThreshold] = useState(10); // Minimum pixels to count as drag
  const [position, setPosition] = useState({
    x: window.innerWidth * 0.45, // Start at 45% from left (roughly center-right)
    y: window.innerHeight - 120   // 120px from bottom
  });

  const dragRef = useRef(null);
  const hasDragged = useRef(false);
  const dragDistance = useRef(0);

  const stats = getQueueStats();

  // Update position on window resize - MOVED BEFORE EARLY RETURN
  useEffect(() => {
    const handleResize = () => {
      const maxX = window.innerWidth - 200;
      const maxY = window.innerHeight - 100;

      setPosition(prev => ({
        x: Math.max(0, Math.min(prev.x, maxX)),
        y: Math.max(0, Math.min(prev.y, maxY))
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const hasActiveItems = uploadQueue.some(item =>
    item.status === 'pending' ||
    item.status === 'uploading'
  );

  if (!isQueueVisible && !hasActiveItems) return null;

  // Unified drag handling for both mouse and touch
  const handleDragStart = (e) => {
    if (!dragRef.current) return;

    // Determine if this is a touch or mouse event
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;

    const rect = dragRef.current.getBoundingClientRect();
    const initialDragStart = {
      x: clientX - rect.left,
      y: clientY - rect.top
    };

    setDragStart(initialDragStart);
    hasDragged.current = false;
    setIsDragging(true);

    // Prevent default behaviors
    e.preventDefault();
    e.stopPropagation();

    // Create unified move handler
    const handleDragMove = (moveEvent) => {
      const isTouchMove = moveEvent.type === 'touchmove';
      const moveClientX = isTouchMove ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const moveClientY = isTouchMove ? moveEvent.touches[0].clientY : moveEvent.clientY;

      hasDragged.current = true;

      const newX = moveClientX - initialDragStart.x;
      const newY = moveClientY - initialDragStart.y;

      // Keep within viewport bounds with some padding
      const padding = 10;
      const maxX = window.innerWidth - 200 - padding;
      const maxY = window.innerHeight - 100 - padding;

      const boundedX = Math.max(padding, Math.min(newX, maxX));
      const boundedY = Math.max(padding, Math.min(newY, maxY));

      setPosition({ x: boundedX, y: boundedY });

      // Prevent scrolling on mobile
      if (isTouchMove) {
        moveEvent.preventDefault();
      }
    };

    // Create unified end handler
    const handleDragEnd = (endEvent) => {
      setIsDragging(false);

      // Remove all event listeners
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('touchend', handleDragEnd);
      document.removeEventListener('touchcancel', handleDragEnd);

      // If we didn't drag, toggle the minimized state
      if (!hasDragged.current) {
        toggleMinimized();
      }
    };

    // Add event listeners for both mouse and touch
    if (isTouch) {
      document.addEventListener('touchmove', handleDragMove, { passive: false });
      document.addEventListener('touchend', handleDragEnd, { passive: false });
      document.addEventListener('touchcancel', handleDragEnd, { passive: false });
    } else {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'secondary';
      case 'uploading': return 'primary';
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'cancelled': return 'warning';
      default: return 'light';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return 'fas fa-clock';
      case 'uploading': return 'fas fa-spinner fa-spin';
      case 'completed': return 'fas fa-check';
      case 'failed': return 'fas fa-exclamation-triangle';
      case 'cancelled': return 'fas fa-times';
      default: return 'fas fa-file';
    }
  };

  const renderMinimizedView = () => (
    <div
      ref={dragRef}
      draggable={false}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 1050,
        backgroundColor: isDragging ? '#f8f9fa' : '#fff',
        border: isDragging ? '2px solid #28a745' : '1px solid #dee2e6', // Green border when dragging
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: isDragging
          ? '0 8px 24px rgba(0,0,0,0.25)'
          : '0 4px 12px rgba(0,0,0,0.15)',
        cursor: isDragging ? 'grabbing' : 'grab',
        minWidth: '200px',
        userSelect: 'none',
        touchAction: 'none', // Prevent scrolling on mobile while dragging
        WebkitUserSelect: 'none', // Safari
        WebkitTouchCallout: 'none', // Safari
        KhtmlUserSelect: 'none', // Konqueror
        MozUserSelect: 'none', // Firefox
        msUserSelect: 'none', // IE
        transform: isDragging ? 'scale(1.02)' : 'scale(1)',
        transition: isDragging ? 'none' : 'all 0.2s ease'
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      onDragStart={(e) => e.preventDefault()} // Prevent default drag behavior
      onContextMenu={(e) => e.preventDefault()} // Prevent context menu on long press
    >
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <small className="text-black">
            <i className="fas fa-grip-dots mr-1"></i>
            Upload Queue
          </small>
          <div className="d-flex align-items-center">
            {stats.uploading > 0 && (
              <i className="fas fa-spinner fa-spin text-primary mr-2"></i>
            )}
            <span className="font-weight-bold text-black">
              {stats.uploading > 0 ? `${stats.uploading} uploading` :
                stats.pending > 0 ? `${stats.pending} pending` :
                  stats.failed > 0 ? `${stats.failed} failed` : 'All done'}
            </span>
          </div>
        </div>
        <i className="fas fa-chevron-up text-black"></i>
      </div>

      {stats.uploading > 0 && (
        <div className="mt-2">
          <ProgressBar
            variant="primary"
            animated
            style={{ height: '4px' }}
            now={50}
          />
        </div>
      )}

      {/* Drag hint */}
      {isDragging && (
        <div className="text-center mt-1">
          <small className="text-black">
            <i className="fas fa-arrows-alt mr-1"></i>
            Drag to reposition or hold to open
          </small>
        </div>
      )}
    </div>
  );

  const renderExpandedView = () => (
    <Modal
      show={true}
      onHide={toggleMinimized}
      size="lg"
      scrollable
      style={{ zIndex: 1055 }}
    >
      <Modal.Header>
        <div className="d-flex align-items-center justify-content-between w-100">
          <Modal.Title>
            <i className="fas fa-cloud-upload-alt mr-2"></i>
            Upload Queue
          </Modal.Title>
          <div className="d-flex align-items-center">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={toggleMinimized}
              className="mr-2"
              title="Minimize"
            >
              <i className="fas fa-minus"></i>
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={hideQueue}
              title="Close"
            >
              <i className="fas fa-times"></i>
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {/* Queue Stats */}
        <div className="upload-queue-stats-container">
          <div className="upload-queue-stats-row">
            <div className="upload-queue-stats-item upload-queue-stats-total">
              <div className="upload-queue-stats-number">{stats.total}</div>
              <small className="upload-queue-stats-label">Total</small>
            </div>
            <div className="upload-queue-stats-item upload-queue-stats-pending">
              <div className="upload-queue-stats-number">{stats.pending}</div>
              <small className="upload-queue-stats-label">Pending</small>
            </div>
            <div className="upload-queue-stats-item upload-queue-stats-uploading">
              <div className="upload-queue-stats-number">{stats.uploading}</div>
              <small className="upload-queue-stats-label">Uploading</small>
            </div>
            <div className="upload-queue-stats-item upload-queue-stats-completed">
              <div className="upload-queue-stats-number">{stats.completed}</div>
              <small className="upload-queue-stats-label">Completed</small>
            </div>
            <div className="upload-queue-stats-item upload-queue-stats-failed">
              <div className="upload-queue-stats-number">{stats.failed}</div>
              <small className="upload-queue-stats-label">Failed</small>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-3 d-flex flex-wrap gap-2">
          {stats.failed > 0 && (
            <Button
              variant="outline-warning"
              size="sm"
              onClick={retryAllFailed}
            >
              <i className="fas fa-redo mr-1"></i>
              Retry All Failed
            </Button>
          )}
          {stats.completed > 0 && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={clearCompleted}
            >
              <i className="fas fa-check mr-1"></i>
              Clear Completed
            </Button>
          )}
          {stats.total > 0 && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={clearAll}
            >
              <i className="fas fa-trash mr-1"></i>
              Clear All
            </Button>
          )}
        </div>

        {/* Upload Items */}
        {uploadQueue.length === 0 ? (
          <Alert variant="info" className="text-center">
            <i className="fas fa-info-circle mr-2"></i>
            No uploads in queue
          </Alert>
        ) : (
          <div className="upload-items">
            {uploadQueue.map((item, index) => (
              <div
                key={item.id}
                className="border rounded p-3 mb-2"
                style={{
                  backgroundColor: item.status === 'uploading' ? '#f8f9ff' : 'white'
                }}
              >
                {/* File Info Header */}
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center flex-grow-1">
                    <i className={`${getStatusIcon(item.status)} mr-2`}></i>
                    <div className="flex-grow-1">
                      <div className="font-weight-medium text-truncate" title={item.fileName}>
                        {item.webkitRelativePath || item.fileName}
                      </div>
                      <small className="text-black">
                        {formatFileSize(item.fileSize)}
                        {item.targetPath && ` → ${item.targetPath}`}
                        {item.isLarge && <Badge variant="warning" className="ml-1">Large</Badge>}
                      </small>
                    </div>
                  </div>

                  <div className="d-flex align-items-center">
                    <Badge variant={getStatusColor(item.status)} className="mr-2">
                      {item.status}
                    </Badge>

                    {/* Reorder buttons */}
                    {(item.status === 'pending') && (
                      <div className="btn-group btn-group-sm mr-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => moveItemUp(item.id)}
                          disabled={index === 0}
                          title="Move up"
                        >
                          <i className="fas fa-chevron-up"></i>
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => moveItemDown(item.id)}
                          disabled={index === uploadQueue.length - 1}
                          title="Move down"
                        >
                          <i className="fas fa-chevron-down"></i>
                        </Button>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="btn-group btn-group-sm">
                      {item.status === 'uploading' && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => cancelUploadWithCleanup(item.id)}
                          title="Cancel Upload"
                        >
                          <i className="fas fa-stop"></i>
                        </Button>
                      )}
                      {item.status === 'failed' && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => retryUpload(item.id)}
                          title="Retry"
                        >
                          <i className="fas fa-redo"></i>
                        </Button>
                      )}
                      {(item.status === 'pending' || item.status === 'failed' || item.status === 'completed' || item.status === 'cancelled') && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeFromQueue(item.id)}
                          title="Remove"
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {(item.status === 'uploading' || item.status === 'completed') && (
                  <div className="mb-2">
                    <ProgressBar
                      now={item.progress}
                      variant={item.status === 'completed' ? 'success' : 'primary'}
                      animated={item.status === 'uploading'}
                      label={`${Math.round(item.progress)}%`}
                      style={{ height: '20px' }}
                    />
                  </div>
                )}

                {/* Status Message */}
                {item.message && (
                  <div className="small text-black mt-1">
                    {item.message}
                  </div>
                )}

                {/* Error Message */}
                {item.error && item.status === 'failed' && (
                  <Alert variant="danger" className="mt-2 mb-0 py-2">
                    <small>
                      <i className="fas fa-exclamation-triangle mr-1"></i>
                      {item.error}
                    </small>
                  </Alert>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );

  return isQueueMinimized ? renderMinimizedView() : renderExpandedView();
};

export default UploadQueueModal;